import React from "react";
import {
  FlaskConical,
  Code2,
  Lightbulb,
  Compass,
  Layers,
  FolderTree,
  ClipboardCheck,
  Webhook,
  ShieldCheck,
  Link2,
  Bug,
  Gauge,
  PenLine,
  ListOrdered,
  FileBarChart,
} from "lucide-react";
import Header from "../Header/Header";
import Footer from "../Footer/footer";
import SEOHead from "../SEO/SEOHead";
import { PageHero, HeroShots } from "../common/ui";
import TeamSpotlight from "../common/TeamSpotlight";
import "../../css/MainPage.css";
import "../../css/Pages.css";
import "../../css/TeamSpotlight.css";

import testerImg from "../../../assets/product/test-cases.webp";
import developerImg from "../../../assets/product/api-testing-create.webp";
import productImg from "../../../assets/product/project-dashboard.webp";
import exploraImg from "../../../assets/product/defects-overview.webp";
import FrothAiIcon from "../common/FrothAiIcon";

const tabs = [
  {
    id: "tester",
    label: "Tester",
    icon: FlaskConical,
    title: "Streamlining test management",
    image: testerImg,
    screenshot: true,
    imageHeight: 700,
    imageAlt: "Test case list with approval status and automation coverage",
    paragraphs: [
      "Testing teams are asked to deliver faster without dropping quality. Froth TestOps, a team-based test management platform, keeps every cycle organised, from AI-assisted test design to execution, review and sign-off, so your team stays in control of every sprint.",
    ],
    cards: [
      {
        icon: Layers,
        title: "Handle different testing cycles smoothly",
        text: "Manage several test cycles at once without losing track of any of them.",
      },
      {
        icon: FrothAiIcon,
        accent: true,
        title: "Let FrothAI write the first draft",
        text: "Generate test scenarios and test cases from requirements, review them, and add only what you need.",
      },
      {
        icon: ClipboardCheck,
        title: "Record outcomes quickly and accurately",
        text: "Execute fast and record every result with evidence, then send it for peer review.",
      },
    ],
  },
  {
    id: "developer",
    label: "Developer",
    icon: Code2,
    title: "Froth TestOps for High-Performing Development Teams",
    image: developerImg,
    screenshot: true,
    imageHeight: 800,
    imageAlt: "API test builder with method, base URL, params, headers, body and auth tabs",
    paragraphs: [
      "Developers want smooth, bug-free releases, but unclear requirements and scattered defects slow them down. With Froth TestOps, developers get clear requirements, linked test cases, API tests and code scanning in one place, which means faster sprints and fewer surprises at release.",
    ],
    cards: [
      {
        icon: FolderTree,
        title: "Create and review detailed test cases",
        text: "Write thorough test cases and review them with teammates for quality assurance.",
      },
      {
        icon: Webhook,
        title: "Test your APIs",
        text: "Build REST API tests with variables, response extraction and data files, then push them to your repository as automation.",
      },
      {
        icon: ShieldCheck,
        title: "Catch vulnerabilities and leaked secrets",
        text: "Scan your GitHub or GitLab repositories with the Code Analyzer before code reaches production.",
      },
    ],
  },
  {
    id: "product",
    label: "Product",
    icon: Lightbulb,
    title: "Froth TestOps for Product-Led Teams",
    image: productImg,
    screenshot: true,
    imageHeight: 562,
    imageAlt: "Project dashboard KPIs and testing progress for the current build",
    paragraphs: [
      "Product teams work to tight deadlines. They need to test quickly, validate fast and release cleanly. Froth TestOps gives product-led teams complete visibility from start to finish, helping them prevent risk, handle user feedback and deliver quality experiences faster.",
    ],
    cards: [
      {
        icon: Link2,
        title: "Requirements",
        text: "Link every test to a requirement, synced with Jira, Plane or Azure DevOps.",
      },
      {
        icon: Bug,
        title: "Issue Tracking",
        text: "Record and track issues in Froth TestOps, or keep them in sync with Jira, Plane or Azure DevOps.",
      },
      {
        icon: Gauge,
        title: "Release Readiness",
        text: "Know at any moment what is ready to release, with project dashboards and a FrothAI release-readiness assessment for every test plan.",
      },
    ],
  },
  {
    id: "exploration",
    label: "Exploration",
    icon: Compass,
    title: "Smart Solutions with Froth TestOps",
    image: exploraImg,
    screenshot: true,
    imageHeight: 338,
    imageAlt: "Defect overview with totals, priority and severity breakdown",
    paragraphs: [
      "Not every team has a strict testing process, and that's perfectly fine. Exploratory testing lets testers think like users, bend the rules and find hidden issues sooner. With Froth TestOps, these teams get an effective defect management module, so they can start testing straight away, note bugs and track their progress.",
    ],
    cards: [
      {
        icon: PenLine,
        title: "Issue Logging",
        text: "Capture and log defects instantly and precisely.",
      },
      {
        icon: ListOrdered,
        title: "Reproduction Steps",
        text: "Document step-by-step instructions so anyone can reproduce an issue.",
      },
      {
        icon: FileBarChart,
        title: "Automated Reporting",
        text: "Generate custom reports and schedule them to be shared with your team automatically, keeping everyone aligned.",
      },
    ],
  },
];

const Flow = () => {
  return (
    <>
      <SEOHead
        title="Team Based Test Management Platform for Efficient QA"
        description="Boost collaboration and efficiency with our team based test management platform, streamlining QA, test execution, and reporting for modern software teams."
        keywords="team based test management platform, QA collaboration, Froth TestOps"
      />
      <div className="fd-main-wrapper page-padding">
        <Header />
        <main>
          <PageHero
            crumbs="Solutions by Team"
            eyebrow="Solutions"
            title={
              <>
                Solutions <span>by Team</span>
              </>
            }
            lead="Testers, developers, product teams and exploratory testers all work differently. Froth TestOps gives each of them the tools they need, in one shared platform."
            visual={
              <HeroShots
                main={productImg}
                mainAlt="Froth TestOps project dashboard"
                float={exploraImg}
                floatAlt="Froth TestOps defects overview"
                mainHeight={562}
                floatHeight={338}
              />
            }
          />
          <TeamSpotlight tabs={tabs} label="Teams" />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Flow;
